'use client';

import { Flex, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import Close from '@material-design-icons/svg/filled/close.svg';
import { useSession } from '@ory/elements-react/client';
import { useCallback, useEffect, useRef } from 'react';
import { SnapshotFrom } from 'xstate';

import {
  LogoutButton,
  OtpVerificationForm,
  PhoneNumberForm,
  UserDetailsForm,
} from '@/components';
import {
  AUTH_MACHINE_LOCAL_STORAGE_KEY,
  useAuthActorRef,
  useAuthSelector,
} from '@/contexts';
import { logger } from '@/utils';

interface LoginFormProps {
  onLoggedIn?: () => void;
  onLoggedOut?: () => void;
  showInterimStates?: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onLoggedIn = () => {},
  onLoggedOut,
  showInterimStates = true,
}) => {
  // Get send function from the actor reference
  const actorRef = useAuthActorRef();

  const { send } = actorRef;

  // Subscribe to state changes and persist to localStorage
  useEffect(() => {
    const subscription = actorRef.subscribe(
      (_snapshot: SnapshotFrom<typeof actorRef>) => {
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(
              AUTH_MACHINE_LOCAL_STORAGE_KEY,
              JSON.stringify(actorRef.getPersistedSnapshot?.()),
            );
          } catch (e) {
            logger.error(`Failed to persist auth state: ${e}`);
          }
        }
      },
    );

    return () => subscription.unsubscribe();
  }, [actorRef]);

  // Use selectors to extract specific values from state
  const context = useAuthSelector((state) => state.context);
  const {
    phoneNumber,
    otpCode,
    errorMessage,
    flowId,
    csrfToken,
    firstName,
    lastName,
    userExistsInHasura,
    userExistsInOry,
  } = context;

  const {
    session,
    isLoading: sessionIsLoading,
    refetch: refetchSession,
  } = useSession();

  // Get current state for matches checks
  const currentState = useAuthSelector((state) => state);
  const isLoading = currentState.hasTag('loading') || sessionIsLoading;
  const isRefetching = useRef(false);

  const handleLoginSuccess = useCallback(async () => {
    if (sessionIsLoading) {
      logger.debug('Session is loading.');
      return;
    }

    logger.debug('Session loaded.');

    if (session) {
      logger.debug('Session post login:', { session });
      onLoggedIn();
      return;
    }

    logger.debug('Session not loaded.');

    if (!isRefetching.current) {
      logger.debug('Refetching session.');
      isRefetching.current = true;
      try {
        await refetchSession();
      } catch (error) {
        logger.error('Failed to refetch session:', { error });
      } finally {
        isRefetching.current = false;
      }
    } else {
      logger.debug('Session refetch already in progress.');
    }
  }, [onLoggedIn, sessionIsLoading, session, refetchSession]);

  // Add logging to track state transitions and events
  useEffect(() => {
    if (currentState.matches('loggedIn')) {
      handleLoginSuccess();
    }
  }, [
    currentState,
    phoneNumber,
    flowId,
    handleLoginSuccess,
    otpCode,
    userExistsInHasura,
    userExistsInOry,
    session,
    sessionIsLoading,
  ]);

  // Display error notifications when errorMessage changes
  useEffect(() => {
    if (errorMessage) {
      notifications.show({
        id: 'login-error',
        title: 'Error',
        message: errorMessage,
        color: 'red',
        icon: <Close />,
      });
    }
  }, [errorMessage]);

  const handleRetry = () => {
    logger.info(
      `Handling retry event, userExistsInHasura: ${userExistsInHasura}`,
    );
    send({ type: 'RETRY' });
  };

  // Render appropriate form based on current state
  const renderForm = () => {
    // Phone number form - idle, checking user, or error states
    if (currentState.hasTag('phoneInput')) {
      logger.debug('Rendering PhoneNumberForm');
      return (
        <PhoneNumberForm
          send={send}
          phoneNumber={phoneNumber}
          isLoading={isLoading}
        />
      );
    }

    // OTP verification form - both for login and registration
    if (currentState.hasTag('otpInput')) {
      return (
        <OtpVerificationForm
          send={send}
          phoneNumber={phoneNumber}
          otpCode={otpCode}
          flowId={flowId}
          csrfToken={csrfToken}
          isLoading={isLoading}
          onRetry={handleRetry}
        />
      );
    }

    // User details form - after OTP verification for new users
    if (currentState.hasTag('userDetails')) {
      return (
        <UserDetailsForm
          send={send}
          firstName={firstName}
          lastName={lastName}
          isLoading={isLoading}
        />
      );
    }

    // Default to phone form
    logger.info('No matching state, defaulting to PhoneNumberForm');
    return (
      <PhoneNumberForm
        send={send}
        isLoading={isLoading}
      />
    );
  };

  return (
    <Stack>
      {currentState.matches('loggedIn') ? (
        <>
          {showInterimStates && (
            <>
              <Text
                mt="xl"
                mb="lg"
              >
                If you are not redirected soon, please logout and retry.
              </Text>
              <Flex
                justify="center"
                mb="xl"
              >
                <LogoutButton onLoggedOut={onLoggedOut} />
              </Flex>
            </>
          )}
        </>
      ) : (
        <Stack gap="lg">
          <Title order={2}>Sign in or create an account</Title>
          {renderForm()}
        </Stack>
      )}
    </Stack>
  );
};

export { LoginForm };
